import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import logo from "../../Image/logo.png";
import { useTheme } from "../../context/ThemeContext";
import Cookies from "js-cookie";

import {
  MdDashboard,
  MdViewStream,
  MdGridView,
  MdHistory,
  MdSchedule,
  MdTune,
  MdDevices,
  MdLink,
  MdCategory,
  MdLanguage,
  MdVideoLibrary,
  MdPerson,
  MdPeople,
  MdCalendarToday,
  MdLocalOffer,
  MdStore,
  MdBuild,
  MdExpandMore,
  MdChevronLeft,
  MdChevronRight,
  MdLogout,
} from "react-icons/md";
import { FiSun, FiMoon } from "react-icons/fi";

/* ── nav item ───────────────────────────────────────────────────── */
const NavItem = ({ link, sidebarOpen, indent = false }) => {
  const Icon = link.icon;
  return (
    <NavLink
      to={link.path}
      end={link.path === "/"}
      title={!sidebarOpen ? link.name : undefined}
      className={({ isActive }) =>
        `relative flex items-center gap-3 py-2.5 rounded-xl transition-all duration-200 group
        ${sidebarOpen ? "px-3" : "px-0 justify-center"}
        ${indent && sidebarOpen ? "ml-3" : ""}
        ${
          isActive
            ? "bg-blue-500/20 text-blue-400"
            : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {/* Active left indicator */}
          {isActive && (
            <span className="absolute left-0 inset-y-2 w-[3px] rounded-r-full bg-blue-500" />
          )}

          <Icon
            size={18}
            className={`shrink-0 transition-colors ${
              isActive ? "text-blue-400" : ""
            }`}
          />

          {sidebarOpen && (
            <span className="text-sm font-medium truncate animate-fade-in">
              {link.name}
            </span>
          )}

          {/* Tooltip when collapsed */}
          {!sidebarOpen && (
            <span
              className="
                absolute left-full ml-4 px-2.5 py-1.5 z-50
                bg-gray-900 text-slate-200 text-xs font-medium
                rounded-lg border border-white/10 shadow-xl
                opacity-0 pointer-events-none whitespace-nowrap
                group-hover:opacity-100 transition-opacity duration-200
              "
            >
              {link.name}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};

/* ── collapsible section ─────────────────────────────────────────── */
const NavSection = ({
  icon: Icon,
  label,
  links,
  isOpen: sectionOpen,
  onToggle,
  sidebarOpen,
}) => (
  <div>
    <button
      onClick={() => sidebarOpen && onToggle()}
      title={!sidebarOpen ? label : undefined}
      className={`w-full flex items-center gap-3 py-2.5 rounded-xl
        text-slate-400 hover:bg-white/5 hover:text-slate-200 transition-all duration-200 group relative
        ${sidebarOpen ? "px-3 justify-between" : "px-0 justify-center"}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={18} className="shrink-0" />
        {sidebarOpen && (
          <span className="text-sm font-medium">{label}</span>
        )}
      </div>
      {sidebarOpen && (
        <MdExpandMore
          className={`text-lg transition-transform duration-300 ${
            sectionOpen ? "rotate-180" : ""
          }`}
        />
      )}

      {/* Tooltip when sidebar collapsed */}
      {!sidebarOpen && (
        <span
          className="
            absolute left-full ml-4 px-2.5 py-1.5 z-50
            bg-gray-900 text-slate-200 text-xs font-medium
            rounded-lg border border-white/10 shadow-xl
            opacity-0 pointer-events-none whitespace-nowrap
            group-hover:opacity-100 transition-opacity duration-200
          "
        >
          {label}
        </span>
      )}
    </button>

    {sidebarOpen && (
      <div
        className={`overflow-hidden transition-all duration-300 ${
          sectionOpen ? "max-h-96" : "max-h-0"
        }`}
      >
        <div className="mt-1 space-y-0.5 border-l border-white/10 ml-4 pl-1">
          {links.map((link) => (
            <NavItem
              key={link.path}
              link={link}
              sidebarOpen={sidebarOpen}
              indent
            />
          ))}
        </div>
      </div>
    )}
  </div>
);

/* ── sidebar ─────────────────────────────────────────────────────── */
const Sidebar = ({ isOpen, setIsOpen }) => {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [prefOpen, setPrefOpen] = useState(false);
  const [bvfOpen, setBvfOpen] = useState(false);
  const username = localStorage.getItem("cw_user") || "User";

  const mainLinks = [
    { name: "Pages",        path: "/",             icon: MdDashboard    },
    { name: "Rows",         path: "/row",           icon: MdViewStream   },
    { name: "Tiles",        path: "/tile",          icon: MdGridView     },
    { name: "Recent Tiles", path: "/recent-tiles",  icon: MdHistory      },
    { name: "Scheduling",   path: "/scheduling",    icon: MdSchedule     },
  ];

  const preferenceLinks = [
    { name: "Source",       path: "/source",        icon: MdLink          },
    { name: "Genres",       path: "/genres",        icon: MdCategory      },
    { name: "Languages",    path: "/languages",     icon: MdLanguage      },
    { name: "Content Type", path: "/content-type",  icon: MdVideoLibrary  },
    { name: "Directors",    path: "/directors",     icon: MdPerson        },
    { name: "Cast Members", path: "/cast-members",  icon: MdPeople        },
    { name: "Year",         path: "/year",          icon: MdCalendarToday },
  ];

  const bvfLinks = [
    { name: "Brand",   path: "/brand",   icon: MdLocalOffer },
    { name: "Vendor",  path: "/vendor",  icon: MdStore      },
    { name: "Factory", path: "/factory", icon: MdBuild      },
  ];

  const handleLogout = () => {
    Cookies.remove("authToken");
    navigate("/login");
  };

  return (
    <>
      {/* ── sidebar panel ──────────────────────────────────────── */}
      <aside
        className={`
          sidebar fixed left-0 top-0 h-screen z-40 flex flex-col
          transition-all duration-300 ease-in-out
          ${isOpen ? "w-60" : "w-16 -translate-x-full md:translate-x-0"}
        `}
      >
        {/* Header */}
        <div
          className={`flex items-center py-4 px-3 shrink-0 ${
            isOpen ? "justify-between" : "justify-center"
          }`}
        >
          {isOpen && (
            <img
              src={logo}
              alt="Cloudwalker"
              className="h-8 object-contain animate-fade-in"
            />
          )}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all duration-200"
            title={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? (
              <MdChevronLeft size={20} />
            ) : (
              <MdChevronRight size={20} />
            )}
          </button>
        </div>

        <div className="mx-3 h-px bg-white/10 shrink-0" />

        {/* Nav — scrollable */}
        <nav className="flex-1 overflow-y-auto no-scrollbar py-3 px-2 space-y-0.5">
          {mainLinks.map((link) => (
            <NavItem
              key={link.path}
              link={link}
              sidebarOpen={isOpen}
            />
          ))}

          <div className="py-2">
            <div className="mx-1 h-px bg-white/10" />
          </div>

          <NavSection
            icon={MdTune}
            label="Preferences"
            links={preferenceLinks}
            isOpen={prefOpen}
            onToggle={() => setPrefOpen((v) => !v)}
            sidebarOpen={isOpen}
          />

          {/* <NavSection
            icon={MdDevices}
            label="BVF"
            links={bvfLinks}
            isOpen={bvfOpen}
            onToggle={() => setBvfOpen((v) => !v)}
            sidebarOpen={isOpen}
          /> */}
        </nav>

        <div className="mx-3 h-px bg-white/10 shrink-0" />

        {/* Footer */}
        <div className="px-2 py-3 shrink-0 space-y-0.5">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            title={isDark ? "Light mode" : "Dark mode"}
            className={`w-full flex items-center gap-3 py-2.5 rounded-xl
              text-slate-400 hover:bg-white/5 hover:text-slate-200 transition-all duration-200
              ${isOpen ? "px-3" : "px-0 justify-center"}`}
          >
            {isDark ? (
              <FiSun size={18} className="shrink-0 text-yellow-400" />
            ) : (
              <FiMoon size={18} className="shrink-0 text-blue-300" />
            )}
            {isOpen && (
              <span className="text-sm font-medium animate-fade-in">
                {isDark ? "Light Mode" : "Dark Mode"}
              </span>
            )}
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            title="Logout"
            className={`w-full flex items-center gap-3 py-2.5 rounded-xl
              text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200
              ${isOpen ? "px-3" : "px-0 justify-center"}`}
          >
            <MdLogout size={18} className="shrink-0" />
            {isOpen && (
              <span className="text-sm font-medium animate-fade-in">
                Logout
              </span>
            )}
          </button>

          {/* User badge */}
          {isOpen && (
            <div className="mt-2 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 animate-fade-in">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-lg">
                  {username.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-200 truncate">
                    {username}
                  </p>
                  <p className="text-xs text-slate-500">Admin</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* ── mobile backdrop ───────────────────────────────────── */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
